// ── Frontend types mirroring backend billing entities ──

export type BillingStatus = 'unbilled' | 'ready_to_bill' | 'on_hold' | 'billed' | 'written_off';

export const BILLING_STATUS_LABELS: Record<BillingStatus, string> = {
  unbilled: 'Unbilled',
  ready_to_bill: 'Ready to Bill',
  on_hold: 'On Hold',
  billed: 'Billed',
  written_off: 'Written Off',
};

export const BILLING_STATUS_TRANSITIONS: Record<BillingStatus, BillingStatus[]> = {
  unbilled: ['ready_to_bill', 'on_hold', 'written_off'],
  ready_to_bill: ['unbilled', 'on_hold', 'billed'],
  on_hold: ['unbilled', 'ready_to_bill', 'written_off'],
  billed: [],
  written_off: ['unbilled'],
};

export const BILLING_STATUS_COLORS: Record<BillingStatus, { bg: string; text: string; darkBg: string; darkText: string }> = {
  unbilled: { bg: 'bg-amber-100', text: 'text-amber-800', darkBg: 'dark:bg-amber-900/20', darkText: 'dark:text-amber-300' },
  ready_to_bill: { bg: 'bg-blue-100', text: 'text-blue-800', darkBg: 'dark:bg-blue-900/20', darkText: 'dark:text-blue-300' },
  on_hold: { bg: 'bg-orange-100', text: 'text-orange-800', darkBg: 'dark:bg-orange-900/20', darkText: 'dark:text-orange-300' },
  billed: { bg: 'bg-green-100', text: 'text-green-800', darkBg: 'dark:bg-green-900/20', darkText: 'dark:text-green-300' },
  written_off: { bg: 'bg-gray-100', text: 'text-gray-600', darkBg: 'dark:bg-gray-700', darkText: 'dark:text-gray-400' },
};

export interface TimeEntry {
  time_entry_id: string;
  realm_id: string;
  matter_id: string;
  user_id: string;
  entry_date: string; // ISO date
  description: string;
  notes?: string | null;
  entry_type: 'hourly' | 'flat_rate';
  is_non_billable: boolean;
  show_on_invoice: boolean;
  activity_category?: string | null;
  utbms_code?: string | null;
  duration: number;
  rate?: number | null;
  amount: number;
  timer_started_at?: string | null;
  timer_status?: 'running' | 'paused' | 'stopped' | null;
  line_item_id?: string | null;
  is_billed: boolean;
  billing_status: BillingStatus;
  billed_date?: string | null;
  requires_approval: boolean;
  is_approved: boolean;
  approved_by_user_id?: string | null;
  approved_at?: string | null;
  ai_source?: 'manual' | 'timer' | 'ai-capture' | null;
  ai_confidence?: number | null;
  ai_draft_status?: 'pending' | 'approved' | 'rejected' | 'edited' | null;
  capture_event_id?: string | null;
  created_at: string;
  updated_at: string;
}

/** Matter relation returned by backend join */
export interface ExpenseMatterRelation {
  matter_id: string;
  matter_number: string;
  client_id: string;
  description?: string;
  client?: {
    client_id: string;
    client_name: string;
  };
}

export interface Expense {
  expense_id: string;
  realm_id: string;
  matter_id: string;
  user_id: string;
  expense_date: string;
  description: string;
  notes?: string | null;
  expense_category: ExpenseCategory;
  utbms_expense_code?: string | null;
  vendor?: string | null;
  reference_number?: string | null;
  is_non_billable: boolean;
  show_on_invoice: boolean;
  quantity: number;
  unit_type?: string | null;
  unit_cost: number;
  total_amount: number;
  is_taxable: boolean;
  tax_rate?: number | null;
  tax_amount: number;
  receipt_attachments?: string[] | null;
  receipt_required: boolean;
  has_receipt: boolean;
  line_item_id?: string | null;
  is_billed: boolean;
  billing_status: BillingStatus;
  billed_date?: string | null;
  requires_approval: boolean;
  is_approved: boolean;
  approved_by_user_id?: string | null;
  approved_at?: string | null;
  ai_categorized: boolean;
  recoverability?: 'billable' | 'absorbed' | 'pending' | null;
  created_at: string;
  updated_at: string;
  /** Populated by backend join — contains matter_number and client info */
  matter?: ExpenseMatterRelation;
}

export type ExpenseCategory =
  | 'travel'
  | 'filing_fee'
  | 'expert_witness'
  | 'court_reporter'
  | 'copying'
  | 'postage'
  | 'other';

export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  travel: 'Travel',
  filing_fee: 'Filing Fee',
  expert_witness: 'Expert Witness',
  court_reporter: 'Court Reporter',
  copying: 'Copying',
  postage: 'Postage',
  other: 'Other',
};

// ── Statistics ──

export interface TimeEntryStatistics {
  total_entries: number;
  total_hours: number;
  billable_hours: number;
  non_billable_hours: number;
  total_amount: number;
  unbilled_entries: number;
  pending_approval: number;
  by_utbms_code?: UtbmsCodeBreakdown[];
  by_activity_category?: ActivityCategoryBreakdown[];
}

export interface ExpenseStatistics {
  total_expenses: number;
  total_amount: number;
  billable_amount: number;
  non_billable_amount: number;
  tax_amount: number;
  unbilled_expenses: number;
  pending_approval: number;
  missing_receipts: number;
  by_category: Record<string, { count: number; amount: number }>;
}

// ── Pagination ──

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: Pagination;
  realm_id: string;
}

// ── Filters ──

export interface TimeEntryFilters {
  matter_id?: string;
  user_id?: string;
  date_from?: string;
  date_to?: string;
  is_billed?: boolean;
  is_non_billable?: boolean;
  requires_approval?: boolean;
  timer_status?: 'running' | 'paused' | 'stopped';
  activity_category?: string;
  billing_status?: BillingStatus | BillingStatus[];
  search?: string;
  limit?: number;
  offset?: number;
  order_by?: string;
  order_direction?: 'ASC' | 'DESC';
}

export interface ExpenseFilters {
  matter_id?: string;
  user_id?: string;
  date_from?: string;
  date_to?: string;
  is_billed?: boolean;
  is_non_billable?: boolean;
  requires_approval?: boolean;
  expense_category?: ExpenseCategory;
  has_receipt?: boolean;
  vendor?: string;
  min_amount?: number;
  max_amount?: number;
  billing_status?: BillingStatus | BillingStatus[];
  search?: string;
  limit?: number;
  offset?: number;
  order_by?: string;
  order_direction?: 'ASC' | 'DESC';
}

// ── DTOs ──

export interface CreateTimeEntryDto {
  matter_id: string;
  entry_date: string;
  description: string;
  entry_type?: 'hourly' | 'flat_rate';
  is_non_billable?: boolean;
  show_on_invoice?: boolean;
  activity_category?: string;
  utbms_code?: string;
  duration?: number;
  rate?: number;
  amount?: number;
  notes?: string;
}

export interface UpdateTimeEntryDto {
  entry_date?: string;
  description?: string;
  entry_type?: 'hourly' | 'flat_rate';
  is_non_billable?: boolean;
  show_on_invoice?: boolean;
  activity_category?: string;
  utbms_code?: string;
  duration?: number;
  rate?: number;
  amount?: number;
  notes?: string;
  requires_approval?: boolean;
}

export interface CreateExpenseDto {
  matter_id: string;
  expense_date: string;
  description: string;
  expense_category: ExpenseCategory;
  utbms_expense_code?: string;
  vendor?: string;
  reference_number?: string;
  is_non_billable?: boolean;
  show_on_invoice?: boolean;
  quantity?: number;
  unit_type?: string;
  unit_cost: number;
  total_amount?: number;
  is_taxable?: boolean;
  tax_rate?: number;
  tax_amount?: number;
  notes?: string;
}

export interface UpdateExpenseDto {
  matter_id?: string;
  expense_date?: string;
  description?: string;
  expense_category?: ExpenseCategory;
  vendor?: string;
  reference_number?: string;
  is_non_billable?: boolean;
  show_on_invoice?: boolean;
  quantity?: number;
  unit_type?: string;
  unit_cost?: number;
  total_amount?: number;
  is_taxable?: boolean;
  tax_rate?: number;
  tax_amount?: number;
  notes?: string;
  requires_approval?: boolean;
}

// ── Revision Reasons (lookup table) ──

export interface RevisionReason {
  revision_reason_id: string;
  realm_id: string;
  code: string;
  label: string;
  description?: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// ── Bills / Invoices ──

export type BillStatus = 'draft' | 'in_review' | 'approved' | 'revision' | 'sent' | 'paid' | 'overdue' | 'cancelled';

export const BILL_STATUS_LABELS: Record<BillStatus, string> = {
  draft: 'Draft',
  in_review: 'In Review',
  approved: 'Approved',
  revision: 'Revision',
  sent: 'Sent',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
};

export interface Bill {
  bill_id: string;
  realm_id: string;
  bill_number: string;
  matter_id: string;
  client_id: string;
  status: BillStatus;
  issue_date: string;
  due_date: string;
  period_start?: string | null;
  period_end?: string | null;
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  total: number;
  amount_paid: number;
  balance: number;
  currency: string;
  description?: string | null;
  memo?: Record<string, unknown> | string | null;
  theme_id?: string | null;
  custom_fields?: Record<string, unknown> | null;
  is_final: boolean;
  is_overdue: boolean;
  auto_generated: boolean;
  revision_count: number;
  revision_reason?: string | null;
  revision_reason_id?: string | null;
  revision_reason_notes?: string | null;
  revision_reason_ref?: RevisionReason | null;
  revision_requested_by_user_id?: string | null;
  revision_requested_at?: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
  // Relations
  matter?: ExpenseMatterRelation;
  line_items?: LineItem[];
}

export interface LineItem {
  line_item_id: string;
  bill_id: string;
  source_activity_id?: string | null;
  source_activity_type: 'time_entry' | 'expense_entry' | 'flat_fee' | 'manual';
  description: string;
  notes?: string | null;
  service_date?: string | null;
  quantity: number;
  rate: number;
  amount: number;
  // Hour/amount cutting fields
  original_quantity?: number | null;
  original_rate?: number | null;
  billed_quantity?: number | null;
  billed_rate?: number | null;
  adjustment_reason?: string | null;
  discount_amount: number;
  discount_percentage?: number | null;
  discount_reason?: string | null;
  net_amount: number;
  is_taxable: boolean;
  tax_rate?: number | null;
  tax_amount: number;
  total_amount: number;
  category?: string | null;
  practice_area?: string | null;
  utbms_code?: string | null;
  sort_order: number;
  requires_approval: boolean;
  is_approved: boolean;
  is_billable: boolean;
  is_write_off: boolean;
  write_off_reason?: string | null;
  show_on_invoice: boolean;
  client_description?: string | null;
  is_confidential: boolean;
  is_locked: boolean;
  modification_history?: Array<{
    timestamp: string;
    user_id: string;
    field: string;
    old_value: unknown;
    new_value: unknown;
  }> | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface BillStatistics {
  total_bills: number;
  total_amount: number;
  total_outstanding: number;
  overdue_count: number;
  by_status: Record<string, { count: number; amount: number }>;
}

export interface BillFilters {
  matter_id?: string;
  client_id?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  is_overdue?: boolean;
  limit?: number;
  offset?: number;
  order_by?: string;
  order_direction?: 'ASC' | 'DESC';
}

export interface LineItemOverride {
  id: string;
  billed_quantity?: number;
  billed_rate?: number;
  adjustment_reason?: string;
}

export interface CreateBillDto {
  matter_id: string;
  description?: string;
  memo?: Record<string, unknown> | string;
  theme_id?: string;
  issue_date?: string;
  due_date?: string;
  period_start?: string;
  period_end?: string;
  include_non_billable?: boolean;
  selected_time_entry_ids?: string[];
  selected_expense_ids?: string[];
  line_item_overrides?: LineItemOverride[];
}

export interface UpdateBillDto {
  description?: string;
  memo?: Record<string, unknown> | string;
  theme_id?: string;
  issue_date?: string;
  due_date?: string;
  period_start?: string;
  period_end?: string;
}

// ── Manual Bill & Line Item DTOs ──

export interface CreateManualBillDto {
  matter_id: string;
  description?: string;
  memo?: Record<string, unknown> | string;
  theme_id?: string;
  issue_date?: string;
  due_date?: string;
  period_start?: string;
  period_end?: string;
  custom_fields?: Record<string, unknown>;
}

export type LineItemActivityType = 'time_entry' | 'expense_entry' | 'flat_fee' | 'manual';

export interface CreateLineItemDto {
  source_activity_type: LineItemActivityType;
  description: string;
  notes?: string;
  service_date?: string;
  quantity: number;
  rate: number;
  discount_amount?: number;
  discount_percentage?: number;
  discount_reason?: string;
  is_taxable?: boolean;
  tax_rate?: number;
  category?: string;
  practice_area?: string;
  utbms_code?: string;
  is_billable?: boolean;
  show_on_invoice?: boolean;
  client_description?: string;
  is_confidential?: boolean;
}

export interface UpdateLineItemDto {
  description?: string;
  notes?: string;
  service_date?: string;
  quantity?: number;
  rate?: number;
  discount_amount?: number;
  discount_percentage?: number;
  discount_reason?: string;
  is_taxable?: boolean;
  tax_rate?: number;
  category?: string;
  practice_area?: string;
  utbms_code?: string;
  is_billable?: boolean;
  show_on_invoice?: boolean;
  client_description?: string;
  is_confidential?: boolean;
}

// ── Invoice Themes ──

export interface InvoiceTheme {
  theme_id: string;
  realm_id: string;
  name: string;
  description?: string | null;
  is_default: boolean;
  is_active: boolean;
  // Logo & Branding
  logo_url?: string | null;
  logo_alignment: 'left' | 'center' | 'right';
  logo_width?: number | null;
  logo_height?: number | null;
  // Firm Branding
  firm_name?: string | null;
  firm_address?: string | null;
  firm_phone?: string | null;
  firm_email?: string | null;
  firm_website?: string | null;
  // Colors
  primary_color: string;
  secondary_color: string;
  background_color: string;
  content_background_color: string;
  // Typography
  font_family: string;
  base_font_size: number;
  header_font_size: number;
  footer_font_size: number;
  line_height: number;
  // Layout toggles
  show_bill_number: boolean;
  show_issue_date: boolean;
  show_due_date: boolean;
  show_billing_period: boolean;
  show_client_information: boolean;
  show_matter_information: boolean;
  // Line item display
  show_line_item_details: boolean;
  show_time_entry_details: boolean;
  show_expense_details: boolean;
  group_by_category: boolean;
  group_by_date: boolean;
  show_activity_descriptions: boolean;
  show_utbms_codes: boolean;
  show_rates_and_hours: boolean;
  show_individual_amounts: boolean;
  // Financial display
  show_subtotal: boolean;
  show_tax_breakdown: boolean;
  show_discounts: boolean;
  show_total_amount: boolean;
  show_amount_paid: boolean;
  show_balance_due: boolean;
  // Payment
  show_payment_instructions: boolean;
  payment_instructions_lexical_json?: Record<string, unknown> | null;
  include_qr_code: boolean;
  payment_portal_url?: string | null;
  // Page setup
  margin_top: number;
  margin_bottom: number;
  margin_left: number;
  margin_right: number;
  page_size: string;
  page_orientation: 'portrait' | 'landscape';
  // Table styling
  show_table_borders: boolean;
  alternate_row_colors: boolean;
  table_header_color: string;
  table_row_color_1: string;
  table_row_color_2: string;
  // Compliance
  include_firm_registration?: boolean;
  firm_registration_text?: string | null;
  include_disclaimer?: boolean;
  disclaimer_lexical_json?: Record<string, unknown> | null;
  // Lexical content
  header_lexical_json?: Record<string, unknown> | null;
  footer_lexical_json?: Record<string, unknown> | null;
  // i18n
  language_code: string;
  default_currency: string;
  number_format_locale: string;
  // Export
  enable_pdf_export: boolean;
  enable_html_export: boolean;
  watermark_draft_bills: boolean;
  watermark_text?: string | null;
  // Usage stats
  usage_count: number;
  last_used_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateInvoiceThemeDto {
  name: string;
  description?: string;
  is_default?: boolean;
  logo_url?: string;
  logo_alignment?: 'left' | 'center' | 'right';
  logo_width?: number;
  logo_height?: number;
  firm_name?: string;
  firm_address?: string;
  firm_phone?: string;
  firm_email?: string;
  firm_website?: string;
  primary_color?: string;
  secondary_color?: string;
  background_color?: string;
  content_background_color?: string;
  font_family?: string;
  base_font_size?: number;
  header_font_size?: number;
  footer_font_size?: number;
  line_height?: number;
  page_size?: string;
  page_orientation?: 'portrait' | 'landscape';
  [key: string]: unknown;
}

export type UpdateInvoiceThemeDto = Partial<CreateInvoiceThemeDto>;

// ── Trust Accounting Types ──

export type TrustAccountType = 'iolta_pooled' | 'dedicated_client' | 'escrow';
export type TrustReconciliationStatus = 'current' | 'overdue' | 'never_reconciled';

export interface TrustAccount {
  trust_account_id: string;
  realm_id: string;
  account_name: string;
  account_type: TrustAccountType;
  bank_name: string;
  account_number_masked?: string;
  routing_number_masked?: string;
  currency: string;
  current_book_balance: number;
  is_active: boolean;
  opened_date?: string;
  closed_date?: string;
  state_bar_jurisdiction?: string;
  overdraft_notification_enabled: boolean;
  last_reconciled_date?: string;
  reconciliation_status: TrustReconciliationStatus;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export type TrustLedgerStatus = 'active' | 'inactive' | 'closed';

export interface ClientTrustLedger {
  client_trust_ledger_id: string;
  realm_id: string;
  trust_account_id: string;
  client_id: string;
  matter_id?: string;
  current_balance: number;
  total_deposits: number;
  total_disbursements: number;
  minimum_balance_threshold?: number;
  is_evergreen: boolean;
  retainer_id?: string;
  status: TrustLedgerStatus;
  created_at: string;
  updated_at: string;
}

export type TrustTransactionType =
  | 'client_deposit' | 'settlement_deposit' | 'escrow_deposit' | 'third_party_deposit'
  | 'court_award_deposit' | 'transfer_in' | 'interest_credit' | 'correction_credit'
  | 'fee_transfer' | 'expense_reimbursement' | 'third_party_payment' | 'settlement_disbursement'
  | 'refund' | 'transfer_out' | 'court_fee_payment' | 'correction_debit';

export type TrustTransactionDirection = 'debit' | 'credit';
export type TrustTransactionStatus = 'pending' | 'completed' | 'reversed' | 'void';
export type TrustPaymentMethod = 'check' | 'wire' | 'ach' | 'cash' | 'credit_card' | 'eft' | 'trust_transfer';

export interface TrustTransaction {
  trust_transaction_id: string;
  realm_id: string;
  trust_account_id: string;
  client_trust_ledger_id: string;
  transaction_type: TrustTransactionType;
  transaction_date: string;
  effective_date?: string;
  amount: number;
  direction: TrustTransactionDirection;
  balance_before: number;
  balance_after: number;
  description: string;
  source_or_payee?: string;
  purpose?: string;
  payment_method?: TrustPaymentMethod;
  reference_number?: string;
  check_number?: string;
  bank_reference?: string;
  related_bill_id?: string;
  transfer_counterpart_id?: string;
  is_cleared: boolean;
  cleared_date?: string;
  is_reconciled: boolean;
  reconciliation_id?: string;
  status: TrustTransactionStatus;
  reversal_of_id?: string;
  reversed_by_id?: string;
  reversal_reason?: string;
  created_by_user_id?: string;
  approved_by_user_id?: string;
  created_at: string;
}

export const TRUST_TRANSACTION_TYPE_LABELS: Record<TrustTransactionType, string> = {
  client_deposit: 'Client Deposit',
  settlement_deposit: 'Settlement Deposit',
  escrow_deposit: 'Escrow Deposit',
  third_party_deposit: 'Third-Party Deposit',
  court_award_deposit: 'Court Award',
  transfer_in: 'Transfer In',
  interest_credit: 'Interest Credit',
  correction_credit: 'Correction (Credit)',
  fee_transfer: 'Fee Transfer',
  expense_reimbursement: 'Expense Reimbursement',
  third_party_payment: 'Third-Party Payment',
  settlement_disbursement: 'Settlement Disbursement',
  refund: 'Refund',
  transfer_out: 'Transfer Out',
  court_fee_payment: 'Court Fee',
  correction_debit: 'Correction (Debit)',
};

export interface RecordTrustDepositDto {
  trust_account_id: string;
  client_id: string;
  matter_id?: string;
  amount: number;
  transaction_type?: TrustTransactionType;
  description: string;
  source_or_payee: string;
  purpose?: string;
  payment_method?: TrustPaymentMethod;
  reference_number?: string;
  check_number?: string;
}

export interface RecordTrustDisbursementDto {
  client_trust_ledger_id: string;
  amount: number;
  description: string;
  source_or_payee: string;
  purpose?: string;
  payment_method?: TrustPaymentMethod;
  reference_number?: string;
  check_number?: string;
}

export interface RecordFeeTransferDto {
  client_trust_ledger_id: string;
  bill_id: string;
  amount: number;
  description?: string;
}

export interface RecordTrustRefundDto {
  client_trust_ledger_id: string;
  amount: number;
  description: string;
  source_or_payee: string;
  payment_method?: TrustPaymentMethod;
  reference_number?: string;
  check_number?: string;
}

// ── UTBMS Codes ──

export type UtbmsCodeSet = 'litigation' | 'counseling' | 'project' | 'bankruptcy' | 'expense';

export interface UtbmsCode {
  code: string;
  code_set: UtbmsCodeSet;
  phase: string | null;
  description: string;
  is_active: boolean;
  sort_order: number;
}

export const UTBMS_CODE_SET_LABELS: Record<UtbmsCodeSet, string> = {
  litigation: 'Litigation (L)',
  counseling: 'Counseling (A)',
  project: 'Project (P)',
  bankruptcy: 'Bankruptcy (B)',
  expense: 'Expense (E)',
};

/** Map from expense_category to default UTBMS expense code */
export const EXPENSE_CATEGORY_TO_UTBMS: Record<string, string> = {
  travel: 'E107',
  filing_fee: 'E115',
  expert_witness: 'E111',
  court_reporter: 'E112',
  copying: 'E101',
  postage: 'E106',
};

// ── Statistics Extensions ──

export interface UtbmsCodeBreakdown {
  code: string;
  count: number;
  hours: number;
  amount: number;
}

export interface ActivityCategoryBreakdown {
  category: string;
  count: number;
  hours: number;
  amount: number;
}

// ── Rate Schedules ──

export type RateType = 'standard' | 'discounted' | 'pro_bono' | 'blended';

export interface RateSchedule {
  rate_schedule_id: string;
  realm_id: string;
  user_id?: string | null;
  client_id?: string | null;
  matter_id?: string | null;
  role?: string | null;
  hourly_rate: number;
  effective_date: string;
  end_date?: string | null;
  rate_type: RateType;
  description?: string | null;
  is_active: boolean;
  priority: number;
  user?: { user_id: string; first_name: string; last_name: string; role: string } | null;
  matter?: { matter_id: string; matter_number: string; description: string } | null;
  created_at: string;
  updated_at: string;
}

export interface CreateRateScheduleDto {
  user_id?: string;
  client_id?: string;
  matter_id?: string;
  role?: string;
  hourly_rate: number;
  effective_date: string;
  end_date?: string;
  rate_type?: RateType;
  description?: string;
  is_active?: boolean;
  priority?: number;
}

export interface UpdateRateScheduleDto extends Partial<CreateRateScheduleDto> {}

export interface RateResolution {
  rate: number;
  source: string;
  schedule_id?: string;
}

// ── Payment types ──

export type PaymentMethod = 'check' | 'credit_card' | 'debit_card' | 'wire_transfer' | 'ach' | 'cash' | 'online_portal' | 'other';
export type PaymentStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'reversed' | 'refunded';
export type ReversalReason = 'nsf' | 'chargeback' | 'stop_payment' | 'ach_return' | 'duplicate' | 'client_dispute' | 'other';

export interface Payment {
  payment_id: string;
  realm_id: string;
  bill_id: string;
  bill?: Bill;
  amount: number;
  currency: string;
  payment_date: string;
  received_date: string;
  payment_method: PaymentMethod;
  reference_number?: string;
  processor_transaction_id?: string;
  processor_name?: string;
  payment_status: PaymentStatus;
  processed_at?: string;
  processing_notes?: string;
  reversal_reason?: ReversalReason;
  processing_fee: number;
  net_amount: number;
  payer_name?: string;
  payer_email?: string;
  card_last_four?: string;
  card_type?: string;
  check_number?: string;
  bank_name?: string;
  is_trust_payment: boolean;
  trust_account_id?: string;
  refunded_amount: number;
  refund_count: number;
  last_refund_date?: string;
  refund_reason?: string;
  unapplied_amount: number;
  notes?: string;
  client_memo?: string;
  recorded_by_user_id: string;
  recorded_at: string;
  created_at: string;
  updated_at: string;
}

export interface RecordPaymentDto {
  bill_id: string;
  amount: number;
  payment_method: PaymentMethod;
  payment_date: string;
  received_date?: string;
  reference_number?: string;
  processor_transaction_id?: string;
  processor_name?: string;
  notes?: string;
  client_memo?: string;
  is_trust_payment?: boolean;
  trust_account_id?: string;
  payer_name?: string;
  payer_email?: string;
  payer_phone?: string;
  card_last_four?: string;
  card_type?: string;
  cardholder_name?: string;
  bank_name?: string;
  check_number?: string;
  check_date?: string;
}

export interface BulkPaymentDto {
  allocations: Array<{ bill_id: string; amount: number }>;
  payment_method: PaymentMethod;
  payment_date: string;
  received_date?: string;
  reference_number?: string;
  total_amount: number;
  notes?: string;
  payer_name?: string;
  is_trust_payment?: boolean;
  trust_account_id?: string;
}

export interface PaymentStatistics {
  total_received: number;
  total_count: number;
  by_method: Array<{ method: string; total: number; count: number }>;
  avg_days_to_payment: number;
  collection_rate: number;
}

// ── Store types ──

export type BillingTab = 'time-entries' | 'expenses' | 'invoices';
export type DrawerMode = 'create' | 'edit';
export type DrawerType = 'time-entry' | 'expense' | 'bill';
